// ── The single CO_* error → honest-posture map (one source of truth) ────────
// Wave 0 foundation, blueprint §2. Mirrors the CRM gold pattern's
// `postureForError` but keyed on the coaching `CO_*` codes and wired to the
// PRESERVED `ApiClientError` (services/api-client.ts) — we did NOT replace the
// working coaching transport. `useHonestQuery` is the only consumer; the
// react-query retry policy reads the resulting posture via NON_RETRYABLE_POSTURES.
//
// Rule (release gate): a gate / denied / not-configured / unavailable response
// must NEVER read as "no results". The map is code-first (stable across HTTP
// nuances) with an HTTP-status fallback.
//
// Contract traps honored (blueprint §1/§7):
//  - ids are strings (irrelevant here, but the envelope is camelCase).
//  - 404 is FAKED via 400 `CO_VALIDATION_FAILED` whose message contains
//    "was not found" — there is NO distinct not-found code.
//  - resource download/upload 503s may be GATE-CLOSED BY DESIGN when the
//    backend includes dark-lane evidence (`downloadUrl:null`, `*Gate:'closed'`).
//  - provider-dark lanes (`generationGate`/`ingestionGate:'closed'`,
//    `meetingProviderStatus:'not_created'`, dry-run) are not-configured/unavailable.

import { ApiClientError } from "./api-client";
import type { ApiClientGate } from "./api-client";
import type { HonestPosture } from "../components/honest-state/honest-posture";

/** The neutral gate descriptor surfaced to <HonestState> for gate-closed copy. */
export interface HonestGate {
  /** true when the lane is intentionally closed (no write happened). */
  closed: boolean;
  /** neutral reason (never a stack/secret/provider name). */
  reason?: string | null;
}

/** Distinguishing resource download/upload 503 path shape vs a real outage. */
const GATE_CLOSED_PATH = /\/resources\/[^/]+\/(?:uploads\/[^/]+\/)?(?:complete|download|upload)(?:\/|$)/i;

/** A 400 message that is really a faked 404 (no distinct not-found code exists). */
const NOT_FOUND_MESSAGE = /was not found|not found/i;

/**
 * posture. `path` is the request path when known, used only to distinguish
 * resource download/upload 503s from a real domain outage. Pass `path` from
 * useHonestQuery's query metadata when available.
 */
export function postureForCoachingError(error: unknown, path?: string): HonestPosture {
  if (!(error instanceof ApiClientError)) return "error";

  const code = (error.code ?? "").toUpperCase();
  const status = error.status;
  const message = error.message ?? "";
  const gate = error.gate;

  // 1. Provider dark-lane gate evidence wins regardless of status: a closed
  //    generation/ingestion gate or an un-provisioned meeting provider is a
  //    stable not-configured/unavailable posture, never an error.
  const gatePosture = postureForGateEvidence(gate);
  if (gatePosture) return gatePosture;

  // 2. Code-driven postures (preferred — stable across HTTP nuances).
  switch (code) {
    case "CO_PERMISSION_DENIED":
      // 401 (no session) is handled by the auth gate (re-auth) and never reaches
      // a content surface; a bare 403 here is a role denial.
      return status === 401 ? "unavailable" : "permission-denied";

    case "CO_DOMAIN_UNAVAILABLE":
      // Resource dark lanes may be intentionally closed when their response
      // carries matching evidence; report downloads are real HTML delivery.
      return isGateClosedShape(error, path) ? "gate-closed" : "unavailable";

    case "CO_MEMBERSHIP_UNAVAILABLE":
    case "CO_AUTH_UNAVAILABLE":
      return "unavailable";

    case "CO_LEGACY_AUTH_REMOVED":
      // 410 — should never reach product UI (auth uses the platform lane).
      return "unavailable";

    case "CO_VALIDATION_FAILED":
      // 404 is faked via a 400 whose message says "was not found".
      return NOT_FOUND_MESSAGE.test(message) ? "empty" : "error";

    case "CO_INTERNAL_AUTH_REQUIRED":
      return "permission-denied";
  }

  // 3. HTTP-status fallback when the code is generic/absent.
  switch (status) {
    case 400:
    case 422:
      return NOT_FOUND_MESSAGE.test(message) ? "empty" : "error";
    case 401:
      return "unavailable";
    case 403:
      return "permission-denied";
    case 404:
      return "empty";
    case 410:
      return "unavailable";
    case 423:
      return "gate-closed";
    case 503:
      return isGateClosedShape(error, path) ? "gate-closed" : "unavailable";
    default:
      return "error";
  }
}

/**
 * Map dark-lane gate evidence to a posture, or null when the evidence does not
 * itself decide the posture (the code/status path then takes over).
 */
function postureForGateEvidence(gate: ApiClientGate | null): HonestPosture | null {
  if (!gate) return null;
  if (gate.generationGate === "closed" || gate.ingestionGate === "closed") {
    // A closed generation/ingestion gate is the provider not being wired yet.
    return "not-configured";
  }
  if (gate.meetingProviderStatus === "not_created") {
    return "not-configured";
  }
  if (gate.downloadUrlNull) {
    // Resource download/upload gate evidence means the lane is intentionally closed.
    return "gate-closed";
  }
  if (gate.dryRun) {
    // Dry-run evidence means the provider lane is dark; nothing was sent.
    return "not-configured";
  }
  return null;
}

/** True for resource download/upload 503-by-design evidence shape. */
function isGateClosedShape(error: ApiClientError, path?: string): boolean {
  if (error.gate?.downloadUrlNull) return true;
  if (error.gate?.generationGate === "closed" || error.gate?.ingestionGate === "closed") return true;
  if (path && GATE_CLOSED_PATH.test(path)) return true;
  return false;
}

/** Extract the neutral gate descriptor for <HonestState>'s gate-closed copy. */
export function honestGateFromError(error: unknown): HonestGate | null {
  if (!(error instanceof ApiClientError)) return null;
  const gate = error.gate;
  if (!gate) return null;
  const closed =
    gate.generationGate === "closed" ||
    gate.ingestionGate === "closed" ||
    gate.meetingProviderStatus === "not_created" ||
    Boolean(gate.downloadUrlNull) ||
    Boolean(gate.dryRun);
  if (!closed && !gate.reason) return null;
  return { closed, reason: gate.reason ?? null };
}

/** The support reference (CO-…) for error/unavailable copy, never a secret. */
export function referenceFromError(error: unknown): string | null {
  return error instanceof ApiClientError ? (error.reference ?? null) : null;
}
